import { useDraggable } from "@dnd-kit/react";
import { AlertDialogTitle } from "@radix-ui/react-alert-dialog";
import { ChevronDown, ChevronRight, Edit2, GripVertical, Mail, RotateCcw, Trash2 } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import { useMatterJurisdiction } from "@/components/contexts/MatterJurisdictionContext";
import { useSidePanel } from "@/components/contexts/SidePanelContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { MailAttachmentIcon } from "@/components/ui/icons";
import { Button } from "@/components/ui/NewButton";
import { renderFileIcon } from "@/lib/document-icons";
import { cleanFileName } from "@/utils/file-utils";
import { useBundleTree, useBundleTreeActions, useInsertIneligibleIds } from "../bundleTreeStore";
import { type FileNode, getEmailFileGuid } from "../bundleTypes";
import { InlineTextEditor } from "../InlineTextEditor";
import { RowActions } from "../RowActions";
import { insertToggleAction } from "../insertToggle";
import { useScrollIntoViewWhen } from "../useScrollIntoViewWhen";
import { useSingleDoubleClick } from "../useSingleDoubleClick";
import { GapDropZone } from "./GapDropZone";

type EditEmailGroupProps = {
  groupId: string;
  scrollTo?: boolean;
  onScrollComplete?: () => void;
};

export const EditEmailGroup = ({ groupId, scrollTo, onScrollComplete }: EditEmailGroupProps) => {
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const config = useBundleTree((s) => s.config);
  const isExpanded = useBundleTree((s) => s.expandedIds.has(groupId));
  const {
    toggleExpand: onToggleExpand,
    updateGroup: onUpdateGroup,
    removeGroup: onRemoveGroup,
    setAttachmentIncluded,
    updateFile: onUpdateFile,
  } = useBundleTreeActions();

  const insertIneligibleIds = useInsertIneligibleIds();

  const group = config.groups[groupId];
  const childIds = group?.children || [];
  const emailFileId = childIds[0];
  const emailFile = emailFileId ? config.files[emailFileId] : undefined;
  const emailFileGuid = getEmailFileGuid(config, groupId);
  const isInsert = emailFile?.isInsert ?? false;
  const attachmentIds = childIds.slice(1);
  const includedAttachmentIds = attachmentIds.filter(id => {
    const f = config.files[id];
    return f && f.includedInBundle !== false;
  });
  const unincludedAttachmentIds = attachmentIds.filter(id => {
    const f = config.files[id];
    return f && f.includedInBundle === false;
  });
  const includedCount = includedAttachmentIds.length;
  const hasAttachments = attachmentIds.length > 0;

  const { ref: sortRef, handleRef, isDragSource } = useDraggable({
    id: groupId,
    data: { type: 'group', groupId },
  });

  const { openDocumentPanel, isOpen, activeDocumentGuid } = useSidePanel();
  const isEmailActive = isOpen && !!emailFileGuid && activeDocumentGuid === emailFileGuid;

  const [editingTitle, setEditingTitle] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const groupRef = useRef<HTMLDivElement | null>(null);

  const clearIsNew = useCallback(() => onUpdateGroup(groupId, { isNew: false }), [onUpdateGroup, groupId]);
  useScrollIntoViewWhen(groupRef, group.isNew, clearIsNew);
  useScrollIntoViewWhen(groupRef, scrollTo, onScrollComplete);

  const handleSubmitTitle = (text: string) => {
    const finalTitle = text.trim() || group.title;
    onUpdateGroup(groupId, { title: finalTitle, isNew: false });
    setEditingTitle(false);
  };

  const titleClickHandlers = useSingleDoubleClick(
    () => { if (emailFileGuid) openDocumentPanel({ documentGuid: emailFileGuid }); },
    () => setEditingTitle(true),
  );

  const EmailIcon = hasAttachments ? MailAttachmentIcon : Mail;

  // Accept gap drops for attachment reorder within this same email group. Email file (index 0)
  // is not reorderable; all other attachments (included or not) can swap.
  const makeAttachmentAcceptFn = (insertBeforeFileId?: string) => (source: any) => {
    if (!source?.data) return false;
    if (source.data.type !== 'file') return false;
    if (source.data.groupId !== groupId) return false;
    const sourceId = source.data.bundleFileId as string;
    if (sourceId === emailFileId) return false;
    const sourceIdx = attachmentIds.indexOf(sourceId);
    if (sourceIdx === -1) return false;
    if (!insertBeforeFileId) return sourceIdx !== attachmentIds.length - 1;
    const targetIdx = attachmentIds.indexOf(insertBeforeFileId);
    return sourceIdx !== targetIdx && sourceIdx !== targetIdx - 1;
  };

  return (
    <div
      ref={(node) => { sortRef(node); groupRef.current = node; }}
      className={`relative transition-colors ${isDragSource ? 'opacity-30' : ''}`}
    >
      <div className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border text-sm group/header transition-colors ${isEmailActive ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'}`}>
        <GripVertical ref={handleRef} className="h-4 w-4 text-gray-300 shrink-0 cursor-grab outline-hidden focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:rounded-sm" />
        {hasAttachments && (
          <Button
            icon={isExpanded ? ChevronDown : ChevronRight}
            variant="bare"
            size="icon-md"
            onClick={() => onToggleExpand(groupId)}
            className="text-gray-500 hover:text-gray-700"
          />
        )}

        {editingTitle ? (
          <>
            <span className="shrink-0"><EmailIcon className="h-4 w-4 text-gray-500" /></span>
            <InlineTextEditor
              initialValue={group.title}
              onSubmit={handleSubmitTitle}
              onCancel={() => setEditingTitle(false)}
              placeholder="Email subject..."
              inputClassName="h-7 text-sm flex-1"
            />
          </>
        ) : (
          <>
            <span
              className="shrink-0 cursor-pointer"
              onClick={() => emailFileGuid && openDocumentPanel({ documentGuid: emailFileGuid })}
            >
              <EmailIcon className="h-4 w-4 text-gray-500" />
            </span>
            <span
              className="text-sm font-medium flex-1 cursor-pointer hover:text-blue-600 transition-colors truncate"
              {...titleClickHandlers}
            >
              {group.title}
              {hasAttachments && (
                <span className="text-xs text-gray-400 font-normal ml-2">
                  {includedCount}/{attachmentIds.length} attachment{attachmentIds.length !== 1 ? 's' : ''}
                </span>
              )}
            </span>
          </>
        )}

        <RowActions
          actions={[
            { icon: Edit2, label: 'Rename email', onClick: () => setEditingTitle(true), visible: !editingTitle },
            insertToggleAction({
              fileId: emailFileId,
              isInsert,
              ineligibleIds: insertIneligibleIds,
              onToggle: (fileId, isInsert) => onUpdateFile(fileId, { isInsert }),
              visible: !editingTitle,
            }),
            { icon: Trash2, label: 'Remove email', onClick: () => setShowDeleteConfirm(true), danger: true },
          ]}
        />
      </div>

      {!isDragSource && isExpanded && hasAttachments && (
        <div className="ml-[18px] pl-3 border-l border-gray-200">
          {includedAttachmentIds.map((fileId) => {
            const file = config.files[fileId];
            if (!file) return null;
            return (
              <React.Fragment key={fileId}>
                <GapDropZone
                  id={`gap-${groupId}-before-${fileId}`}
                  parentGroupId={groupId}
                  insertBeforeId={fileId}
                  accept={makeAttachmentAcceptFn(fileId)}
                />
                <AttachmentRow
                  groupId={groupId}
                  file={file}
                  onToggle={(included) => setAttachmentIncluded(fileId, included)}
                  onRename={(customName) => onUpdateFile(fileId, { customName })}
                />
              </React.Fragment>
            );
          })}
          <GapDropZone
            id={`gap-${groupId}-end`}
            parentGroupId={groupId}
            accept={makeAttachmentAcceptFn()}
          />
          {unincludedAttachmentIds.map((fileId) => {
            const file = config.files[fileId];
            if (!file) return null;
            return (
              <AttachmentRow
                key={fileId}
                groupId={groupId}
                file={file}
                onToggle={(included) => setAttachmentIncluded(fileId, included)}
                onRename={(customName) => onUpdateFile(fileId, { customName })}
              />
            );
          })}
        </div>
      )}

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove &quot;{group.title}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the email{hasAttachments ? ` and all ${attachmentIds.length} attachment${attachmentIds.length !== 1 ? 's' : ''}` : ''} from the {ui_labels.bundle_singular.toLowerCase()}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onRemoveGroup(groupId)} className="bg-red-600 hover:bg-red-700 text-white">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

type AttachmentRowProps = {
  groupId: string;
  file: FileNode;
  onToggle: (included: boolean) => void;
  onRename: (customName: string | null) => void;
};

const AttachmentRow = ({ groupId, file, onToggle, onRename }: AttachmentRowProps) => {
  const { openDocumentPanel, isOpen, activeDocumentGuid } = useSidePanel();
  const included = file.includedInBundle !== false;
  const isActive = isOpen && activeDocumentGuid === file.fileReferenceGuid;
  const checkboxId = `email-${groupId}-${file.fileReferenceGuid}`;
  const { ref, handleRef, isDragSource } = useDraggable({
    id: file.fileId,
    data: { type: 'file', bundleFileId: file.fileId, groupId },
    disabled: !included,
  });

  const originalName = file.fileName;
  const displayName = file.customName || originalName;
  const [editing, setEditing] = useState(false);

  const handleSubmitName = (text: string) => {
    const trimmed = text.trim();
    const isOriginal = !trimmed || trimmed === originalName;
    onRename(isOriginal ? null : trimmed);
    setEditing(false);
  };

  const nameClickHandlers = useSingleDoubleClick(
    () => openDocumentPanel({ documentGuid: file.fileReferenceGuid }),
    () => setEditing(true),
  );

  return (
    <div
      ref={ref}
      className={`flex items-center gap-2 px-2 py-1 rounded-sm text-sm group transition-colors ${isDragSource ? 'opacity-30' : ''} ${isActive ? 'bg-blue-50 border border-blue-300' : 'hover:bg-gray-50'}`}
    >
      {included ? (
        <GripVertical ref={handleRef} className="h-3.5 w-3.5 text-gray-300 shrink-0 cursor-grab outline-hidden focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:rounded-sm" />
      ) : (
        <span className="w-3.5 shrink-0" aria-hidden />
      )}
      <Checkbox
        id={checkboxId}
        checked={included}
        onCheckedChange={(checked) => onToggle(checked === true)}
        className="shrink-0"
      />
      <div
        className="shrink-0 cursor-pointer"
        onClick={() => openDocumentPanel({ documentGuid: file.fileReferenceGuid })}
      >
        {renderFileIcon(`file${file.fileExtension}`, 'h-4 w-4', !included)}
      </div>
      {included && editing ? (
        <InlineTextEditor
          initialValue={displayName}
          onSubmit={handleSubmitName}
          onCancel={() => setEditing(false)}
          inputClassName="h-6 text-sm flex-1"
          iconSize="icon-sm"
        />
      ) : included ? (
        <span
          className="flex-1 truncate text-sm cursor-pointer hover:text-blue-600 transition-colors"
          {...nameClickHandlers}
        >
          {displayName}
          {file.customName && <span className="text-xs text-gray-400 ml-1">(custom)</span>}
        </span>
      ) : (
        <label htmlFor={checkboxId} className="flex-1 truncate text-sm cursor-pointer text-gray-400">
          {cleanFileName(file.fileName)}
        </label>
      )}
      {included && !editing && (
        <RowActions
          hoverGroup="group-hover"
          actions={[
            { icon: Edit2, label: 'Rename', onClick: () => setEditing(true) },
            { icon: RotateCcw, label: 'Revert to original name', onClick: () => onRename(null), visible: !!file.customName, className: 'text-gray-400 hover:text-blue-600' },
          ]}
        />
      )}
      {!included && (
        <span className="text-xs text-gray-400 shrink-0">Not included</span>
      )}
    </div>
  );
};
